import axios from 'axios';
import React, { useEffect, useRef, useState } from "react";
import QRCode from "react-qr-code";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import { Card, CardBody } from "reactstrap";
import { v4 as uuid } from 'uuid';
import { GetUserDetail, QRLogin } from '../../redux/actions/auth';
import { appUrl } from "../../redux/services/base";

// QR code regenerates every 30 seconds.
// After 5 minutes (10 cycles) it stops and shows a "Refresh" overlay.
const QR_REFRESH_INTERVAL_MS = 30000; // 30 seconds
const QR_MAX_CYCLES = 10;             // 10 × 30s = 5 minutes

const QRCodeComp = (props) => {
    const history = useHistory();
    const [qrCodeToken, setQRCodeToken] = useState(null);
    const [sessionId, setSessionId] = useState(null);
    const [expired, setExpired] = useState(false); // true after 5 minutes of inactivity

    const qrCodeTokenRef = useRef(qrCodeToken);
    const verificationTimerRef = useRef(null);
    const qrTimerRef = useRef(null);
    const cycleCountRef = useRef(0); // counts how many QR refreshes have happened

    useEffect(() => {
        let id = uuid();
        setSessionId(id);
        generateQRCode(id);  // Initial QR code generation
        startTimers(id);     // Start regeneration + polling timers
        return () => {
            clearTimers();   // Cleanup on unmount
        };
    }, []);

    useEffect(() => {
        qrCodeTokenRef.current = qrCodeToken;
    }, [qrCodeToken]);

    const clearTimers = () => {
        if (qrTimerRef.current) clearInterval(qrTimerRef.current);
        if (verificationTimerRef.current) clearInterval(verificationTimerRef.current);
    };

    const startTimers = (id) => {
        cycleCountRef.current = 0;

        // QR regeneration timer — stops after QR_MAX_CYCLES cycles
        qrTimerRef.current = setInterval(() => {
            cycleCountRef.current += 1;

            if (cycleCountRef.current >= QR_MAX_CYCLES) {
                // 5 minutes elapsed — stop regenerating and show refresh overlay
                clearInterval(qrTimerRef.current);
                qrTimerRef.current = null;
                setExpired(true);
            } else {
                generateQRCode(id);
            }
        }, QR_REFRESH_INTERVAL_MS);

        // Polling timer — always runs to detect a successful scan
        verificationTimerRef.current = setInterval(() => {
            pollQRCodeLogin();
        }, 5000);
    };

    // Called when user clicks the "Refresh" button after expiry
    const handleRefresh = () => {
        setExpired(false);
        cycleCountRef.current = 0;
        generateQRCode(); // Generate a fresh QR code immediately
        // Restart the regeneration timer
        if (qrTimerRef.current) clearInterval(qrTimerRef.current);
        qrTimerRef.current = setInterval(() => {
            cycleCountRef.current += 1;
            if (cycleCountRef.current >= QR_MAX_CYCLES) {
                clearInterval(qrTimerRef.current);
                qrTimerRef.current = null;
                setExpired(true);
            } else {
                generateQRCode();
            }
        }, QR_REFRESH_INTERVAL_MS);
    };

    const getUserDetails = () => {
        props.dispatch(GetUserDetail()).then(resp => {
            if (resp?.permissions?.can_access_spc) {
                history.push('/spc');
            } else if (resp.permissions?.can_access_jobs) {
                history.push('/jobs');
            } else if (resp.permissions?.can_access_boards) {
                history.push('/boards');
            } else if (resp.permissions?.can_access_forms) {
                history.push('/forms');
            } else {
                history.push('/permission-denied');
            }
        });
    };

    const pollQRCodeLogin = () => {
        const currentToken = qrCodeTokenRef.current;
        if (!currentToken) return;

        axios.get(`${appUrl}/qr-status/${currentToken}/`)
            .then(response => {
                if (response.data.access != null) {
                    clearTimers();
                    props.dispatch(
                        QRLogin(response.data.access, response.data.refresh)
                    ).then(() => {
                        getUserDetails();
                    });
                }
            })
            .catch((err) => {
                console.error('Poll Err > ', err);
            });
    };

    const generateQRCode = (id) => {
        let sid = sessionId || id;
        axios.get(`${appUrl}/qr-session/?session_id=${sid}`)
            .then(response => {
                setQRCodeToken(response.data.data);
            })
            .catch((err) => {
                console.error('Error generating QR code: ', err);
            });
    };

    return (
        <div>
            <Card className="tab2-card">
                <CardBody>
                    <div style={{ marginTop: 20, marginBottom: 20, minHeight: 280 }}>
                        <h2 style={{ textAlign: 'center' }}>Login into STPC</h2>
                        <br />
                        {qrCodeToken == null ? (
                            <span>Loading...</span>
                        ) : (
                            // Wrapper with relative positioning so the overlay sits on top of QR
                            <div style={{
                                position: 'relative',
                                height: 'auto',
                                margin: '0 auto',
                                maxWidth: 150,
                                width: '100%'
                            }}>
                                {/* QR Code — always rendered but blurred when expired */}
                                <QRCode
                                    size={256}
                                    style={{
                                        height: 'auto',
                                        maxWidth: '100%',
                                        width: '100%',
                                        filter: expired ? 'blur(4px)' : 'none',
                                        transition: 'filter 0.3s ease'
                                    }}
                                    value={`@@${qrCodeToken}@@`}
                                    viewBox={`0 0 256 256`}
                                />

                                {/* Refresh overlay — shown only when expired */}
                                {expired && (
                                    <div style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: 'rgba(255, 255, 255, 0.6)',
                                        borderRadius: 4,
                                    }}>
                                        <button
                                            onClick={handleRefresh}
                                            style={{
                                                background: '#001628',
                                                color: '#fff',
                                                border: 'none',
                                                borderRadius: 6,
                                                padding: '8px 16px',
                                                cursor: 'pointer',
                                                fontSize: '0.85rem',
                                                fontWeight: 600,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 6,
                                            }}
                                        >
                                            <i className="fa fa-refresh" aria-hidden="true" />
                                            Refresh
                                        </button>
                                        <span style={{
                                            marginTop: 6,
                                            fontSize: '0.7rem',
                                            color: '#555',
                                            textAlign: 'center'
                                        }}>
                                            QR code expired
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </CardBody>
            </Card>
        </div>
    );
};

function mapStateToProps(state) {
    const { token } = state.auth;
    return { token };
}

export default connect(mapStateToProps)(QRCodeComp);
